// @rpg-unit/enemy-archetypes: four tuned combat enemy archetypes (tier 3; composition framework, decision 0077), cut
// from spike 002's enemies, behaviour trees and enemy rewards: a swarmer (the wisp: fast, weak, darts in one at a time
// when it holds a token), a kiting caster (the shade: keeps 6 to 11 m off, curses the ground, slips away when caught
// close), a brute (slow, tough, owns the ground in front of it) and a phased boss (the warden: a second phase that
// summons and changes its moves). Each is a brain tree on @rpg/ai's standard vocabulary, a sense profile for @rpg/ai's
// perception, a token policy (its priority and when its tree asks), a stat block on standard stat ids
// (@rpg/contracts) and its rewards.
//
// What the game supplies, by id: each archetype's figure card (`card`), its weapon (`weapon`: the moves its tree's
// strikes ask for by intent, see `intentsOf`), its name string, and its drop table (`drops`). A game takes the content
// as its `enemies`, `behaviour-trees` and `enemy-rewards` tables (list the package in `content/include.json`, or add
// `ENEMY_ARCHETYPE_CONTENT` to the tables it loads in the browser), ticks the trees with `standardBrains()`, and tunes
// with `tuneArchetypes` and `tuneRewards`.
import { STANDARD_BRAIN_NAMES, validateTree, type SenseTuning, type TreeJson } from "@rpg/ai";
import { checkStatIds } from "@rpg/contracts";
import enemies from "./content/tables/enemies.json";
import trees from "./content/tables/behaviour-trees.json";
import rewards from "./content/tables/enemy-rewards.json";
import enemiesSchema from "./content/schemas/enemies.schema.json";
import treesSchema from "./content/schemas/behaviour-trees.schema.json";
import rewardsSchema from "./content/schemas/enemy-rewards.schema.json";
import strings from "./strings.json";

export { intentsOf, runArchetype, type LabResult, type LabSetup } from "./lab";

/** This unit's version (the manifest's and package.json's). */
export const ENEMY_ARCHETYPES_VERSION = "0.1.0" as const;

/** A reference into another table (@rpg/content's `x-ref`). */
export interface Ref {
  table: string;
  id: string;
}

/** An archetype's role in a fight. */
export type ArchetypeRole = "swarmer" | "kiting-caster" | "brute" | "phased-boss";

/** One archetype, as its schema describes it (`src/content/schemas/enemies.schema.json`). */
export interface EnemyArchetype {
  id: string;
  archetype: ArchetypeRole;
  name: Ref;
  card: Ref;
  /** Its job in a fight, in a line (not player-facing). */
  job: string;
  tree: Ref;
  hp: number;
  poise: number;
  body: { radius: number; height: number };
  speed: { walk: number; run: number; accel: number; turn: number; separation: number };
  senses: SenseTuning;
  stats: { attack: number; defence: number };
  weapon: Ref;
  tokenPriority: number;
  /** How far its attacks are heard by the others (m). */
  noise: number;
  phases?: { atHp: number; card: Ref; weapon: Ref; speedScale: number; summon?: { kind: Ref; count: number } }[];
}

/** What defeating one gives. */
export interface EnemyReward {
  id: string;
  enemy: Ref;
  xp: number;
  gold: number;
  drops: Ref;
}

/** A behaviour tree row (`TreeJson` plus a note). */
export type ArchetypeTree = TreeJson & { note?: string };

/** The archetypes as a content table. */
export const ENEMY_ARCHETYPES_TABLE = enemies as unknown as { table: "enemies"; version: number; rows: EnemyArchetype[] };
/** Their brains as a content table. */
export const ARCHETYPE_TREES_TABLE = trees as unknown as { table: "behaviour-trees"; version: number; rows: ArchetypeTree[] };
/** Their rewards as a content table. */
export const ARCHETYPE_REWARDS_TABLE = rewards as unknown as { table: "enemy-rewards"; version: number; rows: EnemyReward[] };

/** The tables' row schemas, by table name (the tree schema is @rpg/ai's, written by `pnpm --filter @rpg-unit/enemy-archetypes schema`). */
export const ENEMY_ARCHETYPE_SCHEMAS = {
  enemies: enemiesSchema as Record<string, unknown>,
  "behaviour-trees": treesSchema as Record<string, unknown>,
  "enemy-rewards": rewardsSchema as Record<string, unknown>,
};

/** The content this unit adds to a game, in @rpg/content's shape. */
export const ENEMY_ARCHETYPE_CONTENT = {
  package: "@rpg-unit/enemy-archetypes",
  version: ENEMY_ARCHETYPES_VERSION,
  tables: [ENEMY_ARCHETYPES_TABLE, ARCHETYPE_TREES_TABLE, ARCHETYPE_REWARDS_TABLE],
  schemas: ENEMY_ARCHETYPE_SCHEMAS,
} as const;

/** English defaults for the archetypes' names (`{ id, text }` rows for a strings table). */
export const ENEMY_ARCHETYPE_STRINGS = strings as { id: string; text: string }[];

/** The options the behaviour-tree schema is generated with from @rpg/ai's `behaviourTreeSchema`. */
export const TREE_SCHEMA_OPTIONS = {
  title: "BehaviourTree",
  description:
    "An enemy archetype's brain as data (@rpg-unit/enemy-archetypes; decision 0049): a behaviour tree in @rpg/ai's format naming @rpg/ai's standard brain vocabulary (standardBrains). Generated from @rpg/ai by the unit's `schema` script; edit the package or the unit's TREE_SCHEMA_OPTIONS, not this file.",
  properties: { note: { type: "string" } },
};

/** An archetype's stat block on standard stat ids (@rpg/contracts): its pools start full. */
export function statBlockOf(row: EnemyArchetype): Record<string, number> {
  return { hp: row.hp, hpMax: row.hp, poise: row.poise, poiseMax: row.poise, attack: row.stats.attack, defence: row.stats.defence };
}

/** An archetype's token policy: its priority, whether its tree asks for a token before striking, and with which
 *  intents it strikes. */
export function tokenPolicyOf(row: EnemyArchetype, tree: TreeJson): { priority: number; asks: boolean; intents: string[] } {
  const text = JSON.stringify(tree.root);
  return { priority: row.tokenPriority, asks: text.includes('"askToken"'), intents: [...new Set((text.match(/"intent":"[a-z]+"/g) ?? []).map((m) => m.slice(10, -1)))] };
}

/** Problems across the three tables that their schemas can't say (empty: they hold together): each archetype's tree
 *  exists and names only the standard vocabulary, it asks for a token before it strikes, its stat block is standard,
 *  its senses rise in order and it runs faster than it walks; each summons a kind that exists; each has one reward. */
export function checkArchetypes(o: { enemies: readonly EnemyArchetype[]; trees: readonly ArchetypeTree[]; rewards: readonly EnemyReward[] }): string[] {
  const out: string[] = [];
  const trees = new Map(o.trees.map((t) => [t.id, t]));
  const kinds = new Set(o.enemies.map((e) => e.id));
  for (const t of o.trees) for (const p of validateTree(t, STANDARD_BRAIN_NAMES)) out.push(`tree "${t.id}": ${p}`);
  for (const e of o.enemies) {
    const t = trees.get(e.tree.id);
    if (!t) out.push(`archetype "${e.id}" names a missing tree "${e.tree.id}"`);
    else if (!tokenPolicyOf(e, t).asks) out.push(`archetype "${e.id}": its tree strikes without asking for a token`);
    for (const i of checkStatIds(Object.keys(statBlockOf(e)))) out.push(`archetype "${e.id}": stat "${i.id}" is not standard`);
    if (!(e.senses.alertAt > e.senses.suspiciousAt)) out.push(`archetype "${e.id}": alert (${e.senses.alertAt}) must come after suspicious (${e.senses.suspiciousAt})`);
    if (!(e.speed.run >= e.speed.walk)) out.push(`archetype "${e.id}": runs (${e.speed.run}) slower than it walks (${e.speed.walk})`);
    for (const p of e.phases ?? []) if (p.summon && !kinds.has(p.summon.kind.id)) out.push(`archetype "${e.id}" summons a missing kind "${p.summon.kind.id}"`);
    const r = o.rewards.filter((x) => x.enemy.id === e.id).length;
    if (r !== 1) out.push(`archetype "${e.id}" has ${r} reward rows (want 1)`);
  }
  for (const r of o.rewards) if (!kinds.has(r.enemy.id)) out.push(`reward "${r.id}" is for a missing archetype "${r.enemy.id}"`);
  return out;
}

/** The perceptual knobs (rule 13): what a person would say, and what each changes. */
export const ENEMY_ARCHETYPE_KNOBS = {
  toughness: "tougher or frailer: scales health and poise (1 as tuned; 2 takes twice the hits to kill and to stagger)",
  pace: "faster or slower: scales walking, running, acceleration and turning",
  keenness: "keener or duller: scales how far they see and how fast they react (2 sees twice as far and reacts in half the time)",
  bounty: "richer or poorer: scales the experience and gold a defeat gives (rounded)",
} as const;

/** Knob values; each defaults to 1 (as tuned). */
export interface ArchetypeKnobs {
  toughness?: number;
  pace?: number;
  keenness?: number;
}

const round = (x: number): number => Math.round(x * 1e6) / 1e6;

/** The archetypes turned by the knobs (new rows; the input is untouched). All at 1: equal to the input. */
export function tuneArchetypes(rows: readonly EnemyArchetype[], knobs: ArchetypeKnobs = {}): EnemyArchetype[] {
  const tough = knobs.toughness ?? 1;
  const pace = knobs.pace ?? 1;
  const keen = knobs.keenness ?? 1;
  if (!(tough > 0) || !(pace > 0) || !(keen > 0)) throw new Error(`enemy archetypes: knobs must be > 0 (got ${tough}, ${pace}, ${keen})`);
  return rows.map((r) => {
    const out: EnemyArchetype = structuredClone(r);
    if (tough !== 1) {
      out.hp = round(r.hp * tough);
      out.poise = round(r.poise * tough);
    }
    if (pace !== 1) for (const k of ["walk", "run", "accel", "turn"] as const) out.speed[k] = round(r.speed[k] * pace);
    if (keen !== 1) {
      out.senses.sight.range = round(r.senses.sight.range * keen);
      out.senses.reaction = round(r.senses.reaction / keen);
    }
    return out;
  });
}

/** The rewards turned by the `bounty` knob (xp and gold, rounded to whole numbers). */
export function tuneRewards(rows: readonly EnemyReward[], knobs: { bounty?: number } = {}): EnemyReward[] {
  const b = knobs.bounty ?? 1;
  if (!(b >= 0)) throw new Error(`enemy archetypes: bounty must be ≥ 0 (got ${b})`);
  return rows.map((r) => (b === 1 ? structuredClone(r) : { ...structuredClone(r), xp: Math.round(r.xp * b), gold: Math.round(r.gold * b) }));
}
